<script setup lang="ts">
import Api from '@/services/api/ApiServiceFabrick';
import AuthorityCacheApi from '@/services/models/AuthorityCacheApi';
import { useAuth } from '@/services/user/Auth0UserProvider';
import { useRoute, useRouter } from 'vue-router';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import i18n from '@/lang/i18n';
import getOperationOffice from '@/types/responses/getOperationOffice';
import getAccount from '@/types/responses/getAccount';
import OperationOfficeRow from '@/components/GpsTrackerSetting/OperationOfficeRow.vue';
import TripStartTimeRow from '@/components/GpsTrackerSetting/TripStartTimeRow.vue';
import CommonDriverRow from '@/components/GpsTrackerSetting/CommonDriverRow.vue';

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void;
  switchContentLoading: (loading: boolean) => void;
}>();

const route = useRoute();
const router = useRouter();
const { loginWithRedirect } = useAuth();
const showableUpdateButton = ref(false);
const isUpdating = ref(false);
const gpsTrackerSettingId = ref<number | undefined>(undefined);
const operationOffice = ref<getOperationOffice | null>(null);
const initialOperationOfficeId = ref<number | undefined>(undefined);
const initialDriverAccountId = ref<number | undefined>(undefined);
const tripStartTime = ref('00:00');
const selectedDriver = ref<getAccount | undefined>(undefined);
const errors = ref<Record<string, string[]>>({});

onMounted(async () => {
  props.switchContentLoading(true);
  document.title = i18n.global.t('pages.gpsTrackerSetting.title');
  gpsTrackerSettingId.value = route.params.id ? Number(route.params.id) : undefined;

  AuthorityCacheApi.getWithParse().then((authorityList) => {
    showableUpdateButton.value = !!authorityList['octlink.gps_tracker_settings.update'];
  });

  if (gpsTrackerSettingId.value) {
    const response = await Api().getGpsTrackerSetting(gpsTrackerSettingId.value)
      .catch((error: any) => {
        if (error?.request?.status === 403) {
          props.showFlash(i18n.global.t('pages.common.messages.forbidden'), 'error', true);
          router.push('/gps-tracker-settings');
        }
      });

    if (response) {
      initialOperationOfficeId.value = response.operation_office_id;
      initialDriverAccountId.value = response.common_driver_account_id ?? undefined;
      selectedDriver.value = undefined;
      if (response.trip_start_time) {
        tripStartTime.value = response.trip_start_time.slice(0, 5);
      }
    }
  }

  props.switchContentLoading(false);
});

onBeforeUnmount(() => {
  props.switchContentLoading(false);
});

function makeParams() {
  if (!operationOffice.value) {
    errors.value = {
      operation_office_id: [i18n.global.t('pages.gpsTrackerSetting.messages.operationOfficeRequired')]
    };
    return null;
  }
  return {
    operation_office_id: operationOffice.value.id,
    trip_start_time: tripStartTime.value,
    common_driver_account_id: selectedDriver.value?.id ?? null,
  };
}

function submit() {
  const params = makeParams();
  if (!params) return;
  isUpdating.value = true;
  Api()
    .upsertGpsTrackerSetting(params, gpsTrackerSettingId.value)
    .then(() => {
      props.showFlash(i18n.global.t('pages.common.messages.success'), 'success', true);
      router.push({ name: 'GpsTrackerSettingList' });
    })
    .catch((error: any) => {
      errorHandle(error.response);
    })
    .finally(() => {
      isUpdating.value = false;
    });
}

function errorHandle(res: { status: number; data: { errors: Record<string, string[]> } }) {
  switch (res?.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } });
      break;
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.notEditAuthority'), 'error', true);
      showableUpdateButton.value = false;
      break;
    case 422:
      errors.value = res.data.errors;
      break;
  }
}
</script>

<template>
  <div style="min-height: 45vw">
    <h4 class="pl-3 w-100">{{ i18n.global.t('pages.gpsTrackerSetting.title') }}</h4>
    <div class="mx-3 mb-4">
      <OperationOfficeRow
        v-model="operationOffice"
        :initial-operation-office-id="initialOperationOfficeId"
        :error-messages="errors['operation_office_id']"
        :disabled="!!gpsTrackerSettingId"
      />
      <TripStartTimeRow
        v-model="tripStartTime"
        :error-messages="errors['trip_start_time']"
      />
      <CommonDriverRow
        v-model="selectedDriver"
        :operation-office="operationOffice"
        :initial-driver-account-id="initialDriverAccountId"
        :error-messages="errors['common_driver_account_id']"
      />
      <div v-if="showableUpdateButton" class="d-flex justify-content-end m-3">
        <router-link :to="{ name: 'GpsTrackerSettingList' }">
          <v-btn class="ma-2 reset-btn">
            {{ i18n.global.t('common.cancelBtn') }}
          </v-btn>
        </router-link>
        <v-btn class="ma-2 exec-btn" @click="submit" :loading="isUpdating">
          {{ i18n.global.t('pages.common.button.conservation') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>
